import React, { useEffect, useRef, useState } from "react";
import CustomButton from "../../pages/CustomButton";
import { BASE_URL } from "../../config";
import { getAuthHeaders } from "../../services/useAuth";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
export default function LocationHistoryTable() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeeName, setEmployeeName] = useState("");
  const [workingDate, setWorkingDate] = useState("");
  const [totalRecords, setTotalRecords] = useState(0);
  const navigate = useNavigate();
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const employeeId = params.get("employeeId");
    const working_date = params.get("working_date");

    if (employeeId && working_date) {
      setWorkingDate(working_date);
      fetchLocationReport(employeeId, working_date);
    } else {
      setError("Missing employeeId or working_date parameters");
      setLoading(false);
    }
  }, []);
  const formatDateForAPI = (dateStr) => {
    if (!dateStr) return dateStr;
    if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
      return dateStr;
    }
    try {
      const date = new Date(dateStr);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    } catch (e) {
      return dateStr;
    }
  };
  const formatDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return "";
    try {
      const date = new Date(dateTimeStr);
      if (isNaN(date.getTime())) return dateTimeStr;
      const day = String(date.getDate()).padStart(2, "0");
      const month = date.toLocaleString("en", { month: "short" });
      const year = date.getFullYear();
      const hours = String(date.getHours()).padStart(2, "0");
      const minutes = String(date.getMinutes()).padStart(2, "0");
      return `${day} ${month} ${year} ${hours}:${minutes}`;
    } catch (e) {
      return dateTimeStr;
    }
  };
  const fetchLocationReport = async (employeeId, trackDate) => {
    try {
      setLoading(true);

      const formattedDate = formatDateForAPI(trackDate);

      const response = await fetch(
        `${BASE_URL}/api/employee/${employeeId}/location-report-tp?track_date=${formattedDate}`,
        {
          method: "GET",
          headers: getAuthHeaders(),
        },
      );
      if (!response.ok) {
        throw new Error(`API Error: ${response.status}`);
      }
      const data = await response.json();
      processAPIData(data);
      setLoading(false);
    } catch (err) {
      console.error("Error fetching location report:", err);
      setError(err.message);
      setLoading(false);
    }
  };
  const processAPIData = (data) => {
    if (!data) return;
    if (data.employee) {
      setEmployeeName(data.employee.name || "");
    }
    if (data.visits && Array.isArray(data.visits) && data.visits.length > 0) {
      const visits = data.visits.filter(
        (v) => v.retailer_type !== null && v.retailer_type !== undefined,
      );
      setTotalRecords(data.total_visits ?? visits.length);
      const processedRows = visits.map((v, index) => ({
        sno: index + 1,
        type: v.retailer_type || "",
        code: v.visit_id != null ? String(v.visit_id) : "",
        client: v.retailer_name || "",
        clientId: v.visit_id != null ? String(v.visit_id) : "",
        retailerId: v.retailer_id != null ? String(v.retailer_id) : "",
        retailerType: v.retailer_type || "",
        employeeId:
          data.employee && data.employee.id ? String(data.employee.id) : "",
        pob: v.pob_amount != null ? v.pob_amount : 0,
        city: v.retailer_city || v.retailter_city || "",
        area: v.retailer_area || v.retailter_area || "",
        geoCodeDate: formatDateTime(v.geo_code_date),
        freezeStatus: v.is_geo_freezed ? "Freezed" : "Not Freezed",
        workingDate: formatDateTime(v.visit_date || v.visit_date),
        serverTime: formatDateTime(v.server_time || v.server_time),
        distance: v.accuracy_meter
          ? Number(v.accuracy_meter).toFixed(2)
          : "0.00",
        clientAddress: v.address ?? "",
        employeeLocationTime: formatDateTime(v.visit_date || v.visit_date),
        employeeLatitude: v.employee_latitude ?? v.latitude ?? "",
        employeeLongitude: v.employee_longitude ?? v.longitude ?? "",
      }));

      setRows(processedRows);
      return;
    }
    if (data.locations && Array.isArray(data.locations)) {
      setTotalRecords(data.total_locations ?? data.locations.length);

      const processedRows = data.locations.map((location, index) => ({
        sno: index + 1,
        type: location.activity_type || "",
        code: location.location_id != null ? String(location.location_id) : "",
        client: location.address || "",
        clientId:
          location.location_id != null ? String(location.location_id) : "",
        retailerId:
          location.retailer_id != null ? String(location.retailer_id) : "",
        retailerType: location.retailer_type || "",
        employeeId:
          data.employee && data.employee.id ? String(data.employee.id) : "",
        pob: location.pob_amount || 0,
        city: location.retailer_city || "",
        area: location.retailer_area || "",
        geoCodeDate: formatDateTime(location.geo_code_date),
        freezeStatus: location.is_geo_freezed ? "Freezed" : "Not Freezed",
        workingDate: formatDateTime(location.track_date),
        serverTime: formatDateTime(location.server_time),
        distance: location.accuracy_meter
          ? Number(location.accuracy_meter).toFixed(2)
          : "0.00",
        clientAddress: location.address || "",
        employeeLocationTime: formatDateTime(location.mobile_time),
        employeeLatitude: location.latitude ?? location.employee_latitude ?? "",
        employeeLongitude:
          location.longitude ?? location.employee_longitude ?? "",
      }));

      setRows(processedRows);
    }
  };

  // Sync scroll between header and body
  useEffect(() => {
    const header = headerRef.current;
    const body = bodyRef.current;
    if (!header || !body) return;

    const onBodyScroll = () => {
      header.scrollLeft = body.scrollLeft;
    };
    const onHeaderScroll = () => {
      body.scrollLeft = header.scrollLeft;
    };

    body.addEventListener("scroll", onBodyScroll, { passive: true });
    header.addEventListener("scroll", onHeaderScroll, { passive: true });

    return () => {
      body.removeEventListener("scroll", onBodyScroll);
      header.removeEventListener("scroll", onHeaderScroll);
    };
  }, []);

  // Navigate to client detail
  const handleClientClick = (employeeId, retailerId, retailerType) => {
    if (!employeeId || !retailerId) return;
    const url = `/reporting/client-detail?employeeId=${encodeURIComponent(
      employeeId,
    )}&retailerId=${encodeURIComponent(
      retailerId,
    )}&retailerType=${encodeURIComponent(retailerType)}`;
    window.open(url, "_blank", "width=1400,height=700");
  };

  const handleLocationTimeClick = (lat, lng, time, clientId) => {
    if (!lat || !lng) return;
    const url = `/reporting/location-history?lat=${encodeURIComponent(
      lat,
    )}&lng=${encodeURIComponent(lng)}&time=${encodeURIComponent(
      time || "",
    )}&clientId=${encodeURIComponent(clientId || "")}`;
    window.location.href = url;
  };

  const columns = [
    { key: "sno", label: "S.No" },
    { key: "type", label: "Type" },
    { key: "code", label: "Code" },
    { key: "client", label: "Client" },
    { key: "pob", label: "POB" },
    { key: "city", label: "City" },
    { key: "area", label: "Area" },
    { key: "geoCodeDate", label: "Geo Freeze Date/Time" },
    { key: "freezeStatus", label: "Freeze Status" },
    { key: "workingDate", label: "Working Date" },
    { key: "serverTime", label: "Server Time" },
    { key: "distance", label: "Distance (WIR)" },
    { key: "clientAddress", label: "Freeze Address" },
  ];

  // ✅ Print function
  const handlePrint = () => {
    const printContent = document.getElementById("location-table").outerHTML;
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Print Location Report</title>
          <style>
            table { width: 100%; border-collapse: collapse; font-family: sans-serif; }
            th, td { border: 1px solid #999; padding: 8px; text-align: left; font-size: 12px; }
            th { background: #f0f0f0; }
          </style>
        </head>
        <body>
          <h3>Location Report - ${employeeName} (${workingDate})</h3>
          ${printContent}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  // ✅ Excel export function
  const handleExportExcel = () => {
    if (!rows || rows.length === 0) return alert("No data to export.");
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Location Report");
    XLSX.writeFile(
      workbook,
      `Location_Report_${employeeName}_${workingDate}.xlsx`,
    );
  };

  if (loading) {
    return (
      <div className="w-full bg-white p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-gray-600 text-lg">Loading location data...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white p-4">
        <div className="flex items-center justify-center h-64">
          <div className="text-red-600 text-lg">Error: {error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="mb-4 p-4 bg-gray-50 border-b">
        <h2 className="text-lg font-bold text-gray-800">
          Location Time ({employeeName || "Employee"} - {workingDate})
        </h2>
      </div>

      <div className="overflow-x-auto">
        <table id="location-table" className="unlock-dcr-leave-table text-sm">
          <thead className="bg-blue-500 text-white">
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className="px-4 py-3 text-left font-semibold text-gray-700"
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {rows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className="px-4 py-8 text-center text-gray-500"
                >
                  No location data available
                </td>
              </tr>
            ) : (
              rows.map((row, index) => (
                <tr
                  key={index}
                  className={`hover:bg-gray-50 transition-colors ${
                    index % 2 === 0 ? "bg-white" : "bg-gray-50"
                  }`}
                >
                  {columns.map((col) => {
                    const key = col.key;
                    const value = row[key] ?? "";

                    // Client column with link
                    if (key === "client") {
                      return (
                        <td key={key} className="px-4 py-3 text-gray-900">
                          <div
                            onClick={() =>
                              handleClientClick(
                                row.employeeId,
                                row.retailerId || row.clientId,
                                row.retailerType || "",
                              )
                            }
                            className="text-blue-700 underline cursor-pointer hover:text-blue-900"
                          >
                            {value || "—"}
                          </div>
                        </td>
                      );
                    }

                    // Type and FreezeStatus badge style remain unchanged
                    if (key === "type") {
                      return (
                        <td key={key} className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              value === "General"
                                ? "bg-green-100 text-green-800"
                                : "bg-blue-100 text-blue-800"
                            }`}
                          >
                            {value}
                          </span>
                        </td>
                      );
                    }

                    if (key === "freezeStatus") {
                      return (
                        <td key={key} className="px-4 py-3">
                          <span
                            className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              value === "Freezed"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                            }`}
                          >
                            {value}
                          </span>
                        </td>
                      );
                    }

                    return (
                      <td key={key} className="px-4 py-3 text-gray-900">
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>

        {/* ✅ Buttons */}
        <div className="flex justify-center">
          <div className="flex gap-4 mt-2">
            <CustomButton onClick={handlePrint} label="Print" variant="print">
              🖨
            </CustomButton>
            <CustomButton
              onClick={handleExportExcel}
              label="Export"
              variant="export"
            >
              📥
            </CustomButton>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-4 p-4 border-t bg-gray-50">
        <div className="text-right font-semibold text-gray-700">
          Total Records: {totalRecords || rows.length}
        </div>
      </div>
    </div>
  );
}
